import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowLeft, Copy, Download, PauseCircle, PlayCircle, RefreshCw, ShieldCheck, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { create } from "zustand";

import { api, ApiError } from "../../api/client";
import type { AdminAccess, AdminLicenceCard, AdminLicenceDetail, AdminLive, AdminTerminal, AdminUsage } from "../../api/types";
import { Callout } from "../../components/Callout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Dialog, DialogBody } from "../../components/Dialog";
import { Spinner } from "../../components/EmptyState";
import { PropRow } from "../../components/panel";
import { Motion, Presence } from "../../components/presence";
import { Button, Field, inputClass, Segmented } from "../../components/ui";
import { Waiting } from "../../components/Waiting";
import { t } from "../../i18n/i18n";
import { isPreprod, useEnvironmentValue } from "../environment";

/** Fenêtre « Administration des licences » (demande d'Alexis du 03/11/2026 : « je veux que tout cela soit géré
 *  dans une vue / page admin dans la préprod Generex »), à côté de « Promouvoir… » sur l'accueil de la
 *  préproduction. Trois onglets :
 *
 *  - **Licences** : toutes les licences connues de Generex (unité, client, état, places, postes, dossiers,
 *    dernière activité). Une licence s'ouvre : ses postes avec nom, prénom, grade, service, appareil et présence,
 *    la correction de l'unité et des identités, le message de bandeau, la suspension, la libération d'un poste.
 *  - **Usage** : vues ouvertes et temps passé, exports, requêtes IA, dossiers créés, par période ; export CSV.
 *  - **En direct** : les postes ouverts à l'instant (activité de moins de deux minutes), rafraîchis seuls.
 *
 *  Réservée (demande d'Alexis du 04/11/2026) aux licences autorisées par root sur le serveur
 *  (`sudo generex-preprod-admin autoriser`) : `GET /api/preprod/admin-access` dit si ce poste l'est ; sinon le
 *  bouton n'apparaît pas, toutes les routes de la vue répondent 404, et la fenêtre, si elle s'ouvre malgré tout,
 *  n'affiche que l'écran « réservée » avec la référence de la licence du poste (non secrète) pour l'autoriser.
 *
 *  Jamais un code de licence : une licence se désigne par sa référence d'espace, un poste par son empreinte. En
 *  production, la fenêtre n'existe pas (ses routes répondent 404) et rien n'est appelé. Suspendre la licence de ce
 *  poste ou libérer ce poste est refusé par le serveur (409) : ce serait se fermer la porte. */

export const ADMIN_KEY = ["preprod", "admin"] as const;
const LIVE_MS = 15_000;
const PERIODS = [7, 30, 90] as const;

const ACCESS_KEY = [...ADMIN_KEY, "access"] as const;

const useDialog = create<{ open: boolean }>(() => ({ open: false }));
export const openAdminLicences = () => useDialog.setState({ open: true });

/** Accès de ce poste à la vue, lu en préproduction seulement. */
function useAdminAccess() {
  const preprod = useEnvironmentValue(isPreprod);
  return useQuery({ queryKey: ACCESS_KEY, queryFn: api.preprod.admin.access, enabled: preprod, staleTime: 60_000 });
}

/** Bouton de l'encadré de la préproduction : seulement pour une licence autorisée. */
export function AdminLicencesButton() {
  const access = useAdminAccess();
  return <AdminLicencesEntry allowed={access.data?.allowed === true} />;
}

/** Le bouton lui-même, sans requête (rendu tel quel par les essais). */
export function AdminLicencesEntry({ allowed }: { allowed: boolean }) {
  if (!allowed) return null;
  return (
    <Button variant="secondary" size="sm" onClick={openAdminLicences}>
      <Users className="size-4" />
      {t("Licences…")}
    </Button>
  );
}

/** Commande que root lance sur le serveur pour autoriser la licence de ce poste. */
export const authoriseCommand = (ref: string) => `sudo generex-preprod-admin autoriser ${ref}`;

/** Écran d'une licence non autorisée : la vue est réservée ; référence de la licence du poste et commande pour
 *  l'autoriser. Mode comptes, licence autorisée mais compte non administrateur : seul le motif est dit. */
export function ReservedNotice({ access }: { access: AdminAccess | null }) {
  const [copied, setCopied] = useState(false);
  const ref = access?.reason === "not_listed" ? access.ref : null;
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div data-admin-reserved className="space-y-3">
      <Callout tone="info" title={t("Vue réservée")}>
        {access?.reason === "account_admin"
          ? t("Cette licence peut administrer les licences, mais seulement depuis un compte administrateur de l'unité. Connectez-vous avec un tel compte.")
          : t("L'administration des licences est réservée à la licence de l'administrateur de Generex. La licence de ce poste n'y est pas autorisée.")}
      </Callout>
      {ref ? (
        <>
          <PropRow label={t("Référence de la licence de ce poste")} mono>
            <span className="select-all">{ref}</span>
          </PropRow>
          <p className="text-meta leading-snug text-ink-muted">
            {t("Pour autoriser cette licence, lancez sur le serveur de la préproduction la commande ci-dessous, puis rechargez la page. Cette référence n'est pas secrète : le code de licence ne s'en déduit pas.")}
          </p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 select-all truncate rounded-md border border-line bg-canvas px-2.5 py-2 font-mono text-ui text-ink">
              {authoriseCommand(ref)}
            </code>
            <Button variant="secondary" size="sm" onClick={() => void copy(authoriseCommand(ref))}>
              <Copy className="size-4" />
              {copied ? t("Copiée") : t("Copier")}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Fenêtre, à poser à la racine de l'accueil (préproduction seulement). */
export function AdminLicencesDialogHost() {
  const preprod = useEnvironmentValue(isPreprod);
  const open = useDialog((s) => s.open);
  const shown = preprod && open;
  return (
    <Presence show={shown}>
      {shown ? <AdminLicencesDialog onClose={() => useDialog.setState({ open: false })} /> : null}
    </Presence>
  );
}

type Tab = "licences" | "usage" | "live";

function AdminLicencesDialog({ onClose }: { onClose: () => void }) {
  const access = useAdminAccess();
  if (access.data?.allowed !== true) {
    return (
      <Dialog title={t("Administration des licences")} onClose={onClose} width={560}>
        <DialogBody>
          {access.isPending ? (
            <Waiting>{t("Vérification de l'accès")}</Waiting>
          ) : access.isError ? (
            <Callout
              tone="danger"
              action={
                <Button variant="secondary" size="sm" onClick={() => void access.refetch()}>
                  {t("Réessayer")}
                </Button>
              }
            >
              {t("L'accès à la vue n'a pas pu être vérifié.")}
            </Callout>
          ) : (
            <ReservedNotice access={access.data ?? null} />
          )}
        </DialogBody>
      </Dialog>
    );
  }
  return <AdminLicencesTabs onClose={onClose} />;
}

function AdminLicencesTabs({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<Tab>("licences");
  const [opened, setOpened] = useState<string | null>(null);

  return (
    <Dialog
      title={t("Administration des licences")}
      subtitle={t("Qui détient quelle licence, qui s'en sert, et ce qui s'y passe.")}
      onClose={onClose}
      width={980}
    >
      <div className="border-b border-line px-4 pt-3">
        <Segmented
          value={tab}
          onChange={(value) => {
            setTab(value as Tab);
            setOpened(null);
          }}
          options={[
            { value: "licences", label: t("Licences") },
            { value: "usage", label: t("Usage") },
            { value: "live", label: t("En direct") },
          ]}
        />
      </div>
      <DialogBody className="min-h-[420px]">
        {tab === "licences" ? (
          opened ? (
            <LicenceSheet refValue={opened} onBack={() => setOpened(null)} />
          ) : (
            <LicenceList onOpen={setOpened} />
          )
        ) : null}
        {tab === "usage" ? <UsageTab /> : null}
        {tab === "live" ? <LiveTab /> : null}
      </DialogBody>
    </Dialog>
  );
}

// --- Licences -------------------------------------------------------------------------------------------

const STATE_LABEL: Record<string, string> = {
  active: "Active",
  read_only: "Lecture seule",
  expired: "Expirée",
  suspended: "Suspendue",
  unknown: "Sans poste",
};

const STATE_TONE: Record<string, string> = {
  active: "bg-badge-favorable/15 text-badge-favorable",
  read_only: "bg-ink-soft/15 text-ink-soft",
  expired: "bg-ink-soft/15 text-ink-soft",
  suspended: "bg-danger/15 text-danger",
  unknown: "bg-ink-soft/15 text-ink-soft",
};

export function stateLabel(state: string): string {
  return t(STATE_LABEL[state] ?? "Sans poste");
}

/** « il y a 3 min », « hier », « le 28/10 » : une date d'activité se lit d'un coup d'œil. */
export function sinceText(iso: string | null, now = Date.now()): string {
  if (!iso) return t("jamais");
  const ms = now - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 2) return t("à l'instant");
  if (min < 60) return t("il y a {n} min", { n: min });
  const hours = Math.floor(min / 60);
  if (hours < 24) return t("il y a {n} h", { n: hours });
  const days = Math.floor(hours / 24);
  if (days === 1) return t("hier");
  if (days < 30) return t("il y a {n} jours", { n: days });
  return t("le {date}", { date: new Date(iso).toLocaleDateString() });
}

/** Durée en clair à partir de secondes : « 4 h 20 », « 35 min », « — ». */
export function durationText(seconds: number): string {
  if (!seconds) return "—";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t("{n} min", { n: minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? t("{h} h {m}", { h, m: String(m).padStart(2, "0") }) : t("{h} h", { h });
}

export function sizeText(bytes: number): string {
  if (bytes < 1024) return t("{n} o", { n: bytes });
  if (bytes < 1024 * 1024) return t("{n} Ko", { n: Math.round(bytes / 1024) });
  return t("{n} Mo", { n: Math.round(bytes / (1024 * 1024)) });
}

function StateChip({ state }: { state: string }) {
  return (
    <span className={clsx("rounded-full px-2 py-0.5 text-caption font-medium", STATE_TONE[state])}>
      {stateLabel(state)}
    </span>
  );
}

function LicenceList({ onOpen }: { onOpen: (ref: string) => void }) {
  const query = useQuery({ queryKey: [...ADMIN_KEY, "licences"], queryFn: api.preprod.admin.licences });
  const [search, setSearch] = useState("");

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = query.data ?? [];
    if (!needle) return rows;
    return rows.filter((row) =>
      [row.unit, row.client, row.note].some((value) => (value ?? "").toLowerCase().includes(needle)),
    );
  }, [query.data, search]);

  if (query.isPending) return <Waiting>{t("Lecture des licences")}</Waiting>;
  if (query.isError) {
    return (
      <Callout
        tone="danger"
        action={
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            {t("Réessayer")}
          </Button>
        }
      >
        {t("Les licences n'ont pas pu être lues.")}
      </Callout>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          className={inputClass}
          placeholder={t("Unité, client, note…")}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
          <RefreshCw className="size-4" />
          {t("Actualiser")}
        </Button>
      </div>
      <p className="text-caption text-ink-soft">
        {t("{n} licence(s), {p} poste(s) en service.", {
          n: shown.length,
          p: shown.reduce((total, row) => total + row.terminals, 0),
        })}
      </p>
      <ul className="divide-y divide-line rounded-lg border border-line">
        {shown.map((row) => (
          <li key={row.ref}>
            <button
              type="button"
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-ink/5"
              onClick={() => onOpen(row.ref)}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body font-medium text-ink">
                  {row.unit || t("Unité à saisir")}
                  {row.trial ? <span className="ml-2 text-caption text-ink-soft">{t("essai")}</span> : null}
                </span>
                <span className="block truncate text-caption text-ink-soft">
                  {[row.client, t("{a}/{b} poste(s) identifié(s)", { a: row.terminals_identified, b: row.terminals })]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <span className="shrink-0 text-caption text-ink-soft">{sinceText(row.last_activity)}</span>
              <StateChip state={row.state} />
            </button>
          </li>
        ))}
        {shown.length === 0 ? (
          <li className="px-3 py-6 text-center text-body text-ink-soft">{t("Aucune licence ne correspond.")}</li>
        ) : null}
      </ul>
    </div>
  );
}

function LicenceSheet({ refValue, onBack }: { refValue: string; onBack: () => void }) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: [...ADMIN_KEY, "licence", refValue],
    queryFn: () => api.preprod.admin.licence(refValue),
  });
  const [unit, setUnit] = useState("");
  const [note, setNote] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [toRelease, setToRelease] = useState<AdminTerminal | null>(null);

  useEffect(() => {
    if (query.data) {
      setUnit(query.data.unit ?? "");
      setNote(query.data.note ?? "");
    }
  }, [query.data]);

  const invalidate = (fresh: AdminLicenceDetail) => {
    client.setQueryData([...ADMIN_KEY, "licence", refValue], fresh);
    void client.invalidateQueries({ queryKey: [...ADMIN_KEY, "licences"] });
  };

  const save = useMutation({
    mutationFn: (body: { unit?: string | null; note?: string | null; suspended?: boolean }) =>
      api.preprod.admin.setLicence(refValue, body),
    onSuccess: invalidate,
    onError: (error) => setRefusal(refusalText(error)),
  });

  const release = useMutation({
    mutationFn: (terminal: AdminTerminal) => api.preprod.admin.releaseTerminal(terminal.ref),
    onSuccess: () => void query.refetch(),
    onError: (error) => setRefusal(refusalText(error)),
  });

  if (query.isPending) return <Waiting>{t("Lecture de la licence")}</Waiting>;
  if (query.isError || !query.data) {
    return <Callout tone="danger">{t("Cette licence n'a pas pu être lue.")}</Callout>;
  }

  const licence = query.data;
  const dirty = unit.trim() !== (licence.unit ?? "") || note.trim() !== (licence.note ?? "");

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onBack}>
          <ArrowLeft className="size-4" />
          {t("Toutes les licences")}
        </Button>
        <StateChip state={licence.state} />
      </div>

      <Presence show={Boolean(refusal)}>
        {refusal ? (
          <Motion origin="fade">
            <Callout tone="danger" onClose={() => setRefusal(null)}>
              {refusal}
            </Callout>
          </Motion>
        ) : null}
      </Presence>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("Unité")}>
          <input className={inputClass} value={unit} onChange={(event) => setUnit(event.target.value)} />
        </Field>
        <Field label={t("Note (interne)")}>
          <input className={inputClass} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate({ unit: unit.trim() || null, note: note.trim() || null })}
        >
          {save.isPending ? <Spinner className="size-4" /> : t("Enregistrer")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={save.isPending}
          onClick={() => save.mutate({ suspended: !licence.suspended })}
        >
          {licence.suspended ? <PlayCircle className="size-4" /> : <PauseCircle className="size-4" />}
          {licence.suspended ? t("Rétablir l'accès") : t("Suspendre l'accès")}
        </Button>
        <span className="text-caption text-ink-soft">
          {t("Places : {seats} · Dossiers : {ex} · {size}", {
            seats: licence.seats ?? "—",
            ex: licence.exercises,
            size: sizeText(licence.storage_bytes),
          })}
        </span>
      </div>

      <div>
        <h3 className="mb-2 text-body font-semibold text-ink">
          {t("Postes ({n})", { n: licence.terminals.length })}
        </h3>
        <ul className="divide-y divide-line rounded-lg border border-line">
          {licence.terminals.map((terminal) => (
            <li key={terminal.ref} className="flex items-center gap-3 px-3 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-body text-ink">
                  <span className="truncate font-medium">{personText(terminal)}</span>
                  {terminal.online ? (
                    <span className="inline-flex items-center gap-1 text-caption text-badge-favorable">
                      <span className="size-1.5 rounded-full bg-badge-favorable" />
                      {t("en ligne")}
                    </span>
                  ) : null}
                </span>
                <span className="block truncate text-caption text-ink-soft">
                  {[terminal.service, terminal.device, sinceText(terminal.last_seen)].filter(Boolean).join(" · ")}
                </span>
              </span>
              <Button variant="secondary" size="sm" onClick={() => setToRelease(terminal)}>
                {t("Libérer")}
              </Button>
            </li>
          ))}
          {licence.terminals.length === 0 ? (
            <li className="px-3 py-6 text-center text-body text-ink-soft">{t("Aucun poste en service.")}</li>
          ) : null}
        </ul>
        {licence.terminals.some((terminal) => !terminal.last_name) ? (
          <p className="mt-2 text-caption text-ink-soft">
            {t("Un poste sans nom déclarera son identité à sa prochaine ouverture.")}
          </p>
        ) : null}
      </div>

      <Presence show={Boolean(toRelease)}>
        {toRelease ? (
          <ConfirmDialog
            title={t("Libérer ce poste ?")}
            confirmLabel={t("Libérer")}
            tone="danger"
            onCancel={() => setToRelease(null)}
            onConfirm={() => {
              release.mutate(toRelease);
              setToRelease(null);
            }}
          >
            {t(
              "La place est rendue à la licence et {who} devra saisir le code pour revenir. Les rôles DIREX de ce poste sont retirés.",
              { who: personText(toRelease) },
            )}
          </ConfirmDialog>
        ) : null}
      </Presence>
    </div>
  );
}

export function personText(terminal: Pick<AdminTerminal, "grade" | "first_name" | "last_name" | "name">): string {
  const parts = [terminal.grade, terminal.first_name, terminal.last_name].filter(Boolean);
  return parts.length ? parts.join(" ") : terminal.name || t("Poste sans nom");
}

/** Message d'un refus du serveur : les deux refus propres à la vue sont dits en clair. */
export function refusalText(error: unknown): string {
  if (error instanceof ApiError) {
    const code = error.code ?? (error.detail?.code as string | undefined);
    if (code === "SELF_LICENCE") {
      return t("C'est la licence de ce poste : la suspendre d'ici vous mettrait dehors. Faites-le depuis une autre licence.");
    }
    if (code === "SELF_TERMINAL") return t("C'est ce poste : le libérer d'ici vous mettrait dehors.");
    if (error.message) return error.message;
  }
  return t("L'action n'a pas abouti.");
}

// --- Usage ----------------------------------------------------------------------------------------------

function UsageTab() {
  const [days, setDays] = useState<number>(30);
  const query = useQuery({
    queryKey: [...ADMIN_KEY, "usage", days],
    queryFn: () => api.preprod.admin.usage(days),
  });

  if (query.isPending) return <Waiting>{t("Lecture de l'usage")}</Waiting>;
  if (query.isError || !query.data) return <Callout tone="danger">{t("L'usage n'a pas pu être lu.")}</Callout>;

  const usage: AdminUsage = query.data;
  const total = usage.views.reduce((sum, row) => sum + row.seconds, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          value={String(days)}
          onChange={(value) => setDays(Number(value))}
          fill="natural"
          options={PERIODS.map((period) => ({ value: String(period), label: t("{n} jours", { n: period }) }))}
        />
        <a
          className="ml-auto inline-flex items-center gap-1.5 text-body text-accent hover:underline"
          href={api.preprod.admin.exportUrl(days)}
          download
        >
          <Download className="size-4" />
          {t("Tableur (CSV)")}
        </a>
      </div>

      <div className="grid gap-2 sm:grid-cols-4">
        <PropRow label={t("Temps passé")}>{durationText(total)}</PropRow>
        <PropRow label={t("Dossiers créés")}>{usage.actions.exercises_created}</PropRow>
        <PropRow label={t("Exports")}>{usage.actions.exports}</PropRow>
        <PropRow label={t("Requêtes IA")}>{usage.actions.ai_requests}</PropRow>
      </div>

      <div>
        <h3 className="mb-2 text-body font-semibold text-ink">{t("Vues les plus ouvertes")}</h3>
        <ul className="divide-y divide-line rounded-lg border border-line">
          {usage.views.slice(0, 10).map((row) => (
            <li key={row.view} className="flex items-center gap-3 px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-body text-ink">{row.view}</span>
              <span className="text-caption text-ink-soft">{t("{n} ouverture(s)", { n: row.opens })}</span>
              <span className="w-20 text-right text-body text-ink">{durationText(row.seconds)}</span>
            </li>
          ))}
          {usage.views.length === 0 ? (
            <li className="px-3 py-6 text-center text-body text-ink-soft">{t("Rien sur la période.")}</li>
          ) : null}
        </ul>
      </div>

      <div>
        <h3 className="mb-2 text-body font-semibold text-ink">{t("Postes les plus actifs")}</h3>
        <ul className="divide-y divide-line rounded-lg border border-line">
          {usage.terminals.slice(0, 10).map((row) => (
            <li key={row.ref} className="flex items-center gap-3 px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-body text-ink">{row.name || t("Poste sans nom")}</span>
              <span className="truncate text-caption text-ink-soft">{row.service ?? ""}</span>
              <span className="w-20 text-right text-body text-ink">{durationText(row.seconds)}</span>
            </li>
          ))}
          {usage.terminals.length === 0 ? (
            <li className="px-3 py-6 text-center text-body text-ink-soft">{t("Rien sur la période.")}</li>
          ) : null}
        </ul>
      </div>
      <p className="text-caption text-ink-soft">
        {t("Jamais le contenu des dossiers : seulement les vues ouvertes, le temps passé et les actions comptées.")}
      </p>
    </div>
  );
}

// --- En direct ------------------------------------------------------------------------------------------

function LiveTab() {
  const query = useQuery({
    queryKey: [...ADMIN_KEY, "live"],
    queryFn: api.preprod.admin.live,
    refetchInterval: LIVE_MS,
  });

  if (query.isPending) return <Waiting>{t("Lecture des postes connectés")}</Waiting>;
  if (query.isError || !query.data) return <Callout tone="danger">{t("Les postes n'ont pas pu être lus.")}</Callout>;

  const rows: AdminLive[] = query.data;
  return (
    <div className="space-y-3">
      <p className="flex items-center gap-2 text-caption text-ink-soft">
        <ShieldCheck className="size-4" />
        {t("Postes ouverts à l'instant (activité de moins de deux minutes), rafraîchis seuls.")}
      </p>
      <ul className="divide-y divide-line rounded-lg border border-line">
        {rows.map((row) => (
          <li key={row.terminal} className="flex items-center gap-3 px-3 py-2.5">
            <span className="size-1.5 shrink-0 rounded-full bg-badge-favorable" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-body text-ink">{row.name || t("Poste sans nom")}</span>
              <span className="block truncate text-caption text-ink-soft">
                {[row.unit, row.service, row.view].filter(Boolean).join(" · ")}
              </span>
            </span>
            <span className="shrink-0 text-caption text-ink-soft">
              {row.last_age_s < 60 ? t("à l'instant") : t("il y a {n} min", { n: Math.floor(row.last_age_s / 60) })}
            </span>
          </li>
        ))}
        {rows.length === 0 ? (
          <li className="px-3 py-6 text-center text-body text-ink-soft">{t("Personne n'est connecté.")}</li>
        ) : null}
      </ul>
    </div>
  );
}

export type { AdminLicenceCard };
